import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase/config.js';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth } from '../firebase/config.js';
import { onAuthStateChanged } from 'firebase/auth';

const VolunteerRegistrationSetup = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sections');
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Default configuration structure
  const defaultConfig = {
    sections: {
      personalInfo: {
        enabled: true,
        title: "Personal Information",
        description: "Basic contact and personal details",
        fields: {
          firstName: { enabled: true, required: true, label: "First Name", type: "text" },
          lastName: { enabled: true, required: true, label: "Last Name", type: "text" },
          email: { enabled: true, required: true, label: "Email Address", type: "email" },
          phone: { enabled: true, required: true, label: "Phone Number", type: "tel" },
          address: { enabled: true, required: false, label: "Street Address", type: "text" },
          city: { enabled: true, required: false, label: "City", type: "text" },
          state: { enabled: true, required: false, label: "State", type: "text" },
          zip: { enabled: true, required: false, label: "ZIP Code", type: "text" }
        }
      },
      emergency: {
        enabled: true,
        title: "Emergency Contact",
        description: "Emergency contact information",
        fields: {
          emergencyName: { enabled: true, required: true, label: "Emergency Contact Name", type: "text" },
          emergencyPhone: { enabled: true, required: true, label: "Emergency Contact Phone", type: "tel" },
          emergencyRelation: { enabled: true, required: false, label: "Relationship", type: "text" }
        }
      },
      skills: {
        enabled: true,
        title: "Skills & Experience",
        description: "What skills and experience do you have?",
        fields: {
          skills: { 
            enabled: true, 
            required: false, 
            label: "Skills", 
            type: "multiselect", 
            options: ["Childcare", "Youth Ministry", "Music/Worship", "Teaching", "Administration", "Event Setup", "Food Service", "Technology", "Cleaning", "Greeting/Hospitality", "Security", "Transportation", "Photography", "Audio/Visual", "Maintenance"]
          },
          experience: { enabled: true, required: false, label: "Relevant Experience", type: "textarea" }
        }
      },
      interests: {
        enabled: true,
        title: "Areas of Interest",
        description: "What volunteer opportunities interest you?",
        fields: {
          interests: { 
            enabled: true, 
            required: false, 
            label: "Interests", 
            type: "multiselect", 
            options: ["Sunday Services", "Children's Ministry", "Youth Groups", "Bible Studies", "Community Events", "Outreach Programs", "Food Pantry", "Senior Ministry", "Special Events", "Holiday Programs", "VBS", "Small Groups"]
          }
        }
      },
      availability: {
        enabled: true,
        title: "Availability",
        description: "When are you available to volunteer?",
        fields: {
          availableWeekdays: { enabled: true, required: false, label: "Weekday Availability", type: "multiselect", options: ["Monday Morning", "Monday Afternoon", "Monday Evening", "Tuesday Morning", "Tuesday Afternoon", "Tuesday Evening", "Wednesday Morning", "Wednesday Afternoon", "Wednesday Evening", "Thursday Morning", "Thursday Afternoon", "Thursday Evening", "Friday Morning", "Friday Afternoon", "Friday Evening"] },
          availableWeekends: { enabled: true, required: false, label: "Weekend Availability", type: "multiselect", options: ["Saturday Morning", "Saturday Afternoon", "Saturday Evening", "Sunday Morning", "Sunday Afternoon", "Sunday Evening"] },
          frequency: { enabled: true, required: false, label: "How often are you available?", type: "select", options: ["Weekly", "Bi-weekly", "Monthly", "Special events only", "As needed"] }
        }
      },
      additional: {
        enabled: true,
        title: "Additional Information",
        description: "Any other information you'd like to share",
        fields: {
          referralSource: { enabled: true, required: true, label: "How did you hear about us?", type: "select", options: ["Friend/Family", "Member/Attendee", "Website", "Social Media", "Email", "Event", "Other"] },
          additionalInfo: { enabled: true, required: false, label: "Additional Information", type: "textarea" }
        }
      }
    },
    customFields: [],
    settings: {
      requireAccessCode: true,
      accessCode: "volunteer2024",
      requireApproval: true,
      emailNotifications: true,
      welcomeMessage: "Thank you for your interest in volunteering with us!",
      termsAndConditions: "By registering, you agree to our volunteer policies and understand that a background check may be required for certain positions.",
      enableMultiStep: true,
      requireBackgroundCheck: true
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      try {
        const userQuery = await getDocs(
          query(collection(db, 'faculty_staff'), where('email', '==', user.email))
        );
        
        if (!userQuery.empty) {
          const userData = userQuery.docs[0].data();
          setCurrentUserData({
            ...userData,
            uid: user.uid,
            email: user.email
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    if (user && !authLoading) {
      fetchUserData();
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (currentUserData) {
      loadConfiguration();
    }
  }, [currentUserData]);

  const loadConfiguration = async () => {
    if (!currentUserData?.tenantId) {
      setConfig(defaultConfig);
      setConfigLoading(false);
      return;
    }
    
    try {
      // Read from tenantConfigurations - FIXED: use volunteer_registration not substitute_registration
      const configDoc = await getDoc(
        doc(db, 'tenantConfigurations', `${currentUserData.tenantId}_volunteer_registration`)
      );
      
      if (configDoc.exists()) {
        setConfig(configDoc.data().configValue || defaultConfig);
      } else {
        setConfig(defaultConfig);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      setConfig(defaultConfig);
    }
    setConfigLoading(false);
  };

  const saveConfiguration = async () => {
    setSaving(true);
    setMessage('');
    
    if (!user || !currentUserData?.tenantId) {
      setMessage('Error: User not authenticated or no tenant ID found');
      setSaving(false);
      return;
    }
    
    try {
      // Save to tenantConfigurations - FIXED: use volunteer_registration consistently
      await setDoc(doc(db, 'tenantConfigurations', `${currentUserData.tenantId}_volunteer_registration`), {
        tenantId: currentUserData.tenantId,
        configKey: 'volunteer_registration',
        configValue: config,
        updatedAt: new Date(),
        updatedBy: user.email
      });
      setMessage('Configuration saved successfully!');
    } catch (error) {
      console.error('Error saving configuration:', error);
      setMessage(`Error saving configuration: ${error.message}`);
    }
    
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const toggleSection = (sectionKey) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          enabled: !prev.sections[sectionKey].enabled
        }
      }
    }));
  };

  const toggleField = (sectionKey, fieldKey) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          fields: {
            ...prev.sections[sectionKey].fields,
            [fieldKey]: {
              ...prev.sections[sectionKey].fields[fieldKey],
              enabled: !prev.sections[sectionKey].fields[fieldKey].enabled
            }
          }
        }
      }
    }));
  };

  const toggleFieldRequired = (sectionKey, fieldKey) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          fields: {
            ...prev.sections[sectionKey].fields,
            [fieldKey]: {
              ...prev.sections[sectionKey].fields[fieldKey],
              required: !prev.sections[sectionKey].fields[fieldKey].required
            }
          }
        }
      }
    }));
  };

  const updateFieldLabel = (sectionKey, fieldKey, newLabel) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          fields: {
            ...prev.sections[sectionKey].fields,
            [fieldKey]: {
              ...prev.sections[sectionKey].fields[fieldKey],
              label: newLabel
            }
          }
        }
      }
    }));
  };

  const updateFieldOptions = (sectionKey, fieldKey, options) => {
    setConfig(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          fields: {
            ...prev.sections[sectionKey].fields,
            [fieldKey]: {
              ...prev.sections[sectionKey].fields[fieldKey],
              options: options
            }
          }
        }
      }
    }));
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value
      }
    }));
  };

  const addCustomField = () => {
    const newField = {
      id: `custom_${Date.now()}`,
      label: "New Custom Field",
      type: "text",
      required: false,
      enabled: true
    };
    
    setConfig(prev => ({
      ...prev,
      customFields: [...prev.customFields, newField]
    }));
  };

  const updateCustomField = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      customFields: prev.customFields.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const removeCustomField = (index) => {
    setConfig(prev => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index)
    }));
  };

  if (authLoading || configLoading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading configuration...</div>;
  }

  return (
    <div style={{ padding: '40px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Back Button */}
        <div style={{ marginBottom: '20px' }}>
          <Link 
            to="/volunteer-dashboard"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ← Back to Volunteer Dashboard
          </Link>
        </div>

        <h1 style={{ color: '#1976d2', marginBottom: '10px', fontSize: '32px' }}>Volunteer Registration Setup</h1>
        <p style={{ color: '#666', marginBottom: '30px', fontSize: '16px' }}>
          Configure what information you collect from volunteer applicants
        </p>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '30px', borderBottom: '2px solid #e0e0e0' }}>
          <div style={{ display: 'flex', gap: '0' }}>
            {[
              { key: 'sections', label: 'Form Sections' },
              { key: 'custom', label: 'Custom Fields' },
              { key: 'settings', label: 'Settings' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: activeTab === tab.key ? '#1976d2' : 'transparent',
                  color: activeTab === tab.key ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: activeTab === tab.key ? '600' : '400'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '30px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          
          {/* Form Sections Tab */}
          {activeTab === 'sections' && (
            <div>
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#1976d2' }}>Form Sections & Fields</h2>
              
              {Object.entries(config.sections).map(([sectionKey, section]) => (
                <div key={sectionKey} style={{ marginBottom: '30px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                  {/* Section Header */}
                  <div style={{ 
                    backgroundColor: section.enabled ? '#e3f2fd' : '#f5f5f5',
                    padding: '15px 20px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={section.enabled}
                          onChange={() => toggleSection(sectionKey)}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '18px', fontWeight: '600', color: section.enabled ? '#1976d2' : '#666' }}>
                          {section.title}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Fields */}
                  {section.enabled && (
                    <div style={{ padding: '20px' }}>
                      <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px' }}>{section.description}</p>
                      
                      <div style={{ display: 'grid', gap: '15px' }}>
                        {Object.entries(section.fields).map(([fieldKey, field]) => (
                          <div key={fieldKey} style={{ 
                            padding: '15px',
                            backgroundColor: field.enabled ? '#fafafa' : '#f0f0f0',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0'
                          }}>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: '30px 1fr 80px 120px', 
                              gap: '15px', 
                              alignItems: 'center',
                              marginBottom: field.type === 'multiselect' || field.type === 'select' ? '15px' : '0'
                            }}>
                              {/* Enable checkbox */}
                              <input
                                type="checkbox"
                                checked={field.enabled}
                                onChange={() => toggleField(sectionKey, fieldKey)}
                                style={{ width: '16px', height: '16px' }}
                              />
                              
                              {/* Field label (editable) */}
                              <input
                                type="text"
                                value={field.label}
                                onChange={(e) => updateFieldLabel(sectionKey, fieldKey, e.target.value)}
                                disabled={!field.enabled}
                                style={{
                                  padding: '8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  backgroundColor: field.enabled ? 'white' : '#f5f5f5'
                                }}
                              />
                              
                              {/* Field type */}
                              <span style={{ 
                                fontSize: '12px', 
                                color: '#666',
                                textAlign: 'center',
                                padding: '4px 8px',
                                backgroundColor: '#e3f2fd',
                                borderRadius: '12px'
                              }}>
                                {field.type}
                              </span>
                              
                              {/* Required toggle */}
                              <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                cursor: field.enabled ? 'pointer' : 'not-allowed',
                                fontSize: '14px'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={field.required}
                                  onChange={() => toggleFieldRequired(sectionKey, fieldKey)}
                                  disabled={!field.enabled}
                                  style={{ width: '14px', height: '14px' }}
                                />
                                Required
                              </label>
                            </div>

                            {/* Options editor for select/multiselect fields */}
                            {field.enabled && (field.type === 'select' || field.type === 'multiselect') && (
                              <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                                  Options (one per line):
                                </label>
                                <textarea
                                  value={field.options?.join('\n') || ''}
                                  onChange={(e) => updateFieldOptions(sectionKey, fieldKey, e.target.value.split('\n').filter(o => o.trim()))}
                                  rows={Math.max(3, Math.min(8, field.options?.length || 3))}
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    resize: 'vertical'
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Custom Fields Tab */}
          {activeTab === 'custom' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#1976d2' }}>Custom Fields</h2>
                <button
                  onClick={addCustomField}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  + Add Custom Field
                </button>
              </div>

              {config.customFields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No custom fields added yet. Click "Add Custom Field" to create one.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {config.customFields.map((field, index) => (
                    <div key={field.id} style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '30px 1fr 120px 80px 40px', 
                      gap: '15px', 
                      alignItems: 'center',
                      padding: '15px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px'
                    }}>
                      {/* Enable checkbox */}
                      <input
                        type="checkbox"
                        checked={field.enabled}
                        onChange={(e) => updateCustomField(index, 'enabled', e.target.checked)}
                        style={{ width: '16px', height: '16px' }}
                      />
                      
                      {/* Field label */}
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateCustomField(index, 'label', e.target.value)}
                        placeholder="Field Label"
                        style={{
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      
                      {/* Field type */}
                      <select
                        value={field.type}
                        onChange={(e) => updateCustomField(index, 'type', e.target.value)}
                        style={{
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      >
                        <option value="text">Text</option>
                        <option value="email">Email</option>
                        <option value="tel">Phone</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                        <option value="select">Dropdown</option>
                        <option value="multiselect">Multi-select</option>
                        <option value="textarea">Text Area</option>
                      </select>
                      
                      {/* Required toggle */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateCustomField(index, 'required', e.target.checked)}
                          style={{ width: '14px', height: '14px' }}
                        />
                        Required
                      </label>
                      
                      {/* Remove button */}
                      <button
                        onClick={() => removeCustomField(index)}
                        style={{
                          padding: '8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div>
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#1976d2' }}>Registration Settings</h2>
              
              <div style={{ display: 'grid', gap: '25px' }}>
                {/* Multi-step Form */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.settings.enableMultiStep}
                      onChange={(e) => updateSetting('enableMultiStep', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>Enable Multi-step Form</span>
                  </label>
                  <p style={{ margin: '5px 0 0 26px', fontSize: '14px', color: '#666' }}>
                    Break the form into multiple steps for better user experience
                  </p>
                </div>

                {/* Access Code */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.settings.requireAccessCode}
                      onChange={(e) => updateSetting('requireAccessCode', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>Require Access Code</span>
                  </label>
                  {config.settings.requireAccessCode && (
                    <input
                      type="text"
                      value={config.settings.accessCode}
                      onChange={(e) => updateSetting('accessCode', e.target.value)}
                      placeholder="Enter access code"
                      style={{
                        width: '300px',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginLeft: '26px'
                      }}
                    />
                  )}
                  <p style={{ margin: '5px 0 0 26px', fontSize: '14px', color: '#666' }}>
                    Prevents spam registrations by requiring a code
                  </p>
                </div>

                {/* Approval Required */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.settings.requireApproval}
                      onChange={(e) => updateSetting('requireApproval', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>Require Manual Approval</span>
                  </label>
                  <p style={{ margin: '5px 0 0 26px', fontSize: '14px', color: '#666' }}>
                    All registrations must be approved by an administrator
                  </p>
                </div>

                {/* Background Check */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.settings.requireBackgroundCheck}
                      onChange={(e) => updateSetting('requireBackgroundCheck', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>Require Background Check Consent</span>
                  </label>
                  <p style={{ margin: '5px 0 0 26px', fontSize: '14px', color: '#666' }}>
                    Require volunteers to consent to background checks
                  </p>
                </div>

                {/* Email Notifications */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.settings.emailNotifications}
                      onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>Send Email Notifications</span>
                  </label>
                  <p style={{ margin: '5px 0 0 26px', fontSize: '14px', color: '#666' }}>
                    Send emails when applications are submitted and approved
                  </p>
                </div>

                {/* Welcome Message */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500' }}>
                    Welcome Message
                  </label>
                  <textarea
                    value={config.settings.welcomeMessage}
                    onChange={(e) => updateSetting('welcomeMessage', e.target.value)}
                    placeholder="Enter welcome message for applicants"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Terms and Conditions */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500' }}>
                    Terms and Conditions
                  </label>
                  <textarea
                    value={config.settings.termsAndConditions}
                    onChange={(e) => updateSetting('termsAndConditions', e.target.value)}
                    placeholder="Enter terms and conditions text"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
            <button
              onClick={saveConfiguration}
              disabled={saving}
              style={{
                padding: '12px 30px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            
            {message && (
              <div style={{
                marginTop: '15px',
                padding: '10px',
                backgroundColor: message.includes('Error') ? '#f8d7da' : '#d4edda',
                color: message.includes('Error') ? '#721c24' : '#155724',
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolunteerRegistrationSetup;